import { useCallback, useEffect, useState } from "react";
import { useAppFeedback } from "../../../useAppFeedback";
import {
    advanceTournament,
    createTournament,
    CreateTournamentInput,
    deleteTournament,
    eliminateTeam,
    reinstateTeam,
    listTournaments,
    Tournament,
    TournamentMatch,
    TournamentStatus,
    TournamentStyle,
    TournamentVisibility,
    TournamentTeam,
    updateMatch,
    UpdateMatchPayload,
    updateTournament,
    getTournament,
} from "../../../../services/beerpongService";
import { AuthUser } from "../../../../context/AuthContext";
import { useTranslation } from "react-i18next";

interface UseTournamentManagerResult {
    tournaments: Tournament[];
    loadingTournaments: boolean;
    creatingTournament: boolean;
    updatingMatchId: number | null;
    updatingTournamentId: number | null;
    unlockingTournamentId: number | null;
    eliminatingTeamId: number | null;
    advancingTournamentId: number | null;
    restoringTeamId: number | null;
    deletingTournamentId: number | null;
    loadTournaments: () => Promise<void>;
    createTournamentEntry: (
        input: CreateTournamentInput,
        user: AuthUser,
        hasByeTeam: boolean
    ) => Promise<boolean>;
    updateMatchWinner: (
        tournament: Tournament,
        match: TournamentMatch,
        payload: { winnerTeamId: number | null }
    ) => Promise<void>;
    updateMatchTeams: (
        tournament: Tournament,
        match: TournamentMatch,
        payload: Pick<UpdateMatchPayload, "teamAId" | "teamBId">
    ) => Promise<void>;
    updateTournamentStatus: (tournament: Tournament, status: TournamentStatus) => Promise<void>;
    updateTournamentStyle: (tournament: Tournament, style: TournamentStyle) => Promise<void>;
    updateTournamentVisibility: (
        tournament: Tournament,
        visibility: TournamentVisibility,
        password?: string | null
    ) => Promise<boolean>;
    eliminateTournamentTeam: (tournament: Tournament, team: TournamentTeam) => Promise<void>;
    restoreTournamentTeam: (tournament: Tournament, team: TournamentTeam) => Promise<void>;
    advanceTournamentBracket: (tournament: Tournament) => Promise<void>;
    unlockTournamentDetails: (tournament: Tournament, password: string) => Promise<void>;
    deleteTournamentEntry: (tournament: Tournament) => Promise<void>;
}

export const useTournamentManager = (currentUser?: AuthUser | null): UseTournamentManagerResult => {
    const { message } = useAppFeedback();
    const [tournaments, setTournaments] = useState<Tournament[]>([]);
    const [loadingTournaments, setLoadingTournaments] = useState<boolean>(false);
    const [creatingTournament, setCreatingTournament] = useState<boolean>(false);
    const [updatingMatchId, setUpdatingMatchId] = useState<number | null>(null);
    const [updatingTournamentId, setUpdatingTournamentId] = useState<number | null>(null);
    const [unlockingTournamentId, setUnlockingTournamentId] = useState<number | null>(null);
    const [eliminatingTeamId, setEliminatingTeamId] = useState<number | null>(null);
    const [advancingTournamentId, setAdvancingTournamentId] = useState<number | null>(null);
    const [restoringTeamId, setRestoringTeamId] = useState<number | null>(null);
    const [deletingTournamentId, setDeletingTournamentId] = useState<number | null>(null);

    const { t } = useTranslation();

    const loadTournaments = useCallback(async () => {
        setLoadingTournaments(true);
        try {
            const response = await listTournaments(currentUser ?? undefined);
            setTournaments(response);
        } catch (error) {
            console.error("Failed to load tournaments", error);
            message.error(t("beerpong.tournament.messages.loadTournamentsError"));
        } finally {
            setLoadingTournaments(false);
        }
    }, [currentUser, t, message]);

    useEffect(() => {
        loadTournaments();
    }, [loadTournaments]);

    const createTournamentEntry = useCallback(
        async (input: CreateTournamentInput, user: AuthUser, hasByeTeam: boolean) => {
            setCreatingTournament(true);
            try {
                const created = await createTournament(input, user);
                setTournaments((prev) => [created, ...prev]);
                message.success(t("beerpong.tournament.messages.createSuccess"));
                if (hasByeTeam) {
                    message.info(
                        t("beerpong.tournament.messages.byeInfo")
                    );
                }
                return true;
            } catch (error) {
                console.error("Tournament creation failed", error);
                message.error(t("beerpong.tournament.messages.createError"));
                return false;
            } finally {
                setCreatingTournament(false);
            }
        },
        [t, message]
    );

    const applyUpdatedTournament = useCallback((updated: Tournament) => {
        setTournaments((prev) =>
            prev.map((candidate) => (candidate.tournamentId === updated.tournamentId ? updated : candidate))
        );
    }, []);

    const runMatchUpdate = useCallback(
        async (
            tournament: Tournament,
            match: TournamentMatch,
            payload: UpdateMatchPayload,
            successMessageKey: string
        ) => {
            if (!currentUser) {
                message.error(t("beerpong.tournament.messages.signInToUpdate"));
                return;
            }
            setUpdatingMatchId(match.matchId);
            try {
                await updateMatch(match.matchId, payload, currentUser);
                const refreshed = await getTournament(tournament.tournamentId, currentUser);
                applyUpdatedTournament(refreshed);
                message.success(t(successMessageKey));
            } catch (error) {
                console.error("Failed to update match", error);
                message.error(t("beerpong.tournament.messages.updateMatchError"));
            } finally {
                setUpdatingMatchId(null);
            }
        },
        [applyUpdatedTournament, currentUser, t, message]
    );

    const updateMatchWinner = useCallback(
        async (tournament: Tournament, match: TournamentMatch, payload: { winnerTeamId: number | null }) => {
            await runMatchUpdate(tournament, match, payload, "beerpong.tournament.messages.matchUpdated");
        },
        [runMatchUpdate]
    );

    const updateMatchTeams = useCallback(
        async (
            tournament: Tournament,
            match: TournamentMatch,
            payload: Pick<UpdateMatchPayload, "teamAId" | "teamBId">
        ) => {
            await runMatchUpdate(tournament, match, payload, "beerpong.tournament.messages.matchPairingUpdated");
        },
        [runMatchUpdate]
    );

    const updateTournamentStatus = useCallback(
        async (tournament: Tournament, status: TournamentStatus) => {
            if (!currentUser) {
                message.error(t("beerpong.tournament.messages.signInToUpdate"));
                return;
            }
            setUpdatingTournamentId(tournament.tournamentId);
            try {
                const updated = await updateTournament(tournament.tournamentId, { status }, currentUser);
                applyUpdatedTournament(updated);
                message.success(t("beerpong.tournament.messages.tournamentUpdated"));
            } catch (error) {
                console.error("Failed to update tournament status", error);
                message.error(t("beerpong.tournament.messages.updateStatusError"));
            } finally {
                setUpdatingTournamentId(null);
            }
        },
        [applyUpdatedTournament, currentUser, t, message]
    );

    const updateTournamentStyle = useCallback(
        async (tournament: Tournament, style: TournamentStyle) => {
            if (!currentUser) {
                message.error(t("beerpong.tournament.messages.signInToUpdate"));
                return;
            }
            setUpdatingTournamentId(tournament.tournamentId);
            try {
                const updated = await updateTournament(tournament.tournamentId, { style }, currentUser);
                applyUpdatedTournament(updated);
                message.success(t("beerpong.tournament.messages.styleUpdated"));
            } catch (error) {
                console.error("Failed to update tournament style", error);
                message.error(t("beerpong.tournament.messages.styleUpdateError"));
            } finally {
                setUpdatingTournamentId(null);
            }
        },
        [applyUpdatedTournament, currentUser, t, message]
    );

    const updateTournamentVisibility = useCallback(
        async (tournament: Tournament, visibility: TournamentVisibility, password?: string | null) => {
            if (!currentUser) {
                message.error(t("beerpong.tournament.messages.signInToUpdate"));
                return false;
            }
            setUpdatingTournamentId(tournament.tournamentId);
            try {
                const updated = await updateTournament(
                    tournament.tournamentId,
                    { visibility, password: password ?? null },
                    currentUser
                );
                applyUpdatedTournament(updated);
                message.success(t("beerpong.tournament.messages.accessUpdated"));
                return true;
            } catch (error) {
                console.error("Failed to update tournament visibility", error);
                message.error(t("beerpong.tournament.messages.accessUpdateError"));
                return false;
            } finally {
                setUpdatingTournamentId(null);
            }
        },
        [applyUpdatedTournament, currentUser, t, message]
    );

    const unlockTournamentDetails = useCallback(
        async (tournament: Tournament, password: string) => {
            const trimmedPassword = password.trim();
            if (!trimmedPassword) {
                message.warning(t("beerpong.tournament.messages.enterPassword"));
                return;
            }
            setUnlockingTournamentId(tournament.tournamentId);
            try {
                const unlocked = await getTournament(tournament.tournamentId, currentUser ?? undefined, trimmedPassword);
                applyUpdatedTournament(unlocked);
                message.success(t("beerpong.tournament.messages.unlockSuccess"));
            } catch (error) {
                console.error("Failed to unlock tournament", error);
                message.error(t("beerpong.tournament.messages.unlockError"));
            } finally {
                setUnlockingTournamentId(null);
            }
        },
        [applyUpdatedTournament, currentUser, t, message]
    );

    const eliminateTournamentTeam = useCallback(
        async (tournament: Tournament, team: TournamentTeam) => {
            if (!currentUser) {
                message.error(t("beerpong.tournament.messages.signInToUpdate"));
                return;
            }
            setEliminatingTeamId(team.teamId);
            try {
                const updated = await eliminateTeam(tournament.tournamentId, team.teamId, currentUser);
                applyUpdatedTournament(updated);
                message.success(
                    t("beerpong.tournament.messages.teamEliminated", { team: team.teamName })
                );
            } catch (error) {
                console.error("Failed to eliminate team", error);
                message.error(t("beerpong.tournament.messages.teamEliminateError"));
            } finally {
                setEliminatingTeamId(null);
            }
        },
        [applyUpdatedTournament, currentUser, t, message]
    );

    const advanceTournamentBracket = useCallback(
        async (tournament: Tournament) => {
            if (!currentUser) {
                message.error(t("beerpong.tournament.messages.signInToUpdate"));
                return;
            }
            setAdvancingTournamentId(tournament.tournamentId);
            try {
                const updated = await advanceTournament(tournament.tournamentId, currentUser);
                applyUpdatedTournament(updated);
                message.success(t("beerpong.tournament.messages.advanceSuccess"));
            } catch (error) {
                console.error("Failed to advance tournament", error);
                message.error(t("beerpong.tournament.messages.advanceError"));
            } finally {
                setAdvancingTournamentId(null);
            }
        },
        [applyUpdatedTournament, currentUser, t, message]
    );

    const restoreTournamentTeam = useCallback(
        async (tournament: Tournament, team: TournamentTeam) => {
            if (!currentUser) {
                message.error(t("beerpong.tournament.messages.signInToUpdate"));
                return;
            }
            setRestoringTeamId(team.teamId);
            try {
                const updated = await reinstateTeam(tournament.tournamentId, team.teamId, currentUser);
                applyUpdatedTournament(updated);
                await loadTournaments();
                message.success(
                    t("beerpong.tournament.messages.teamRestored", { team: team.teamName })
                );
            } catch (error) {
                console.error("Failed to restore team", error);
                message.error(t("beerpong.tournament.messages.teamRestoreError"));
            } finally {
                setRestoringTeamId(null);
            }
        },
        [applyUpdatedTournament, currentUser, loadTournaments, t, message]
    );

    const deleteTournamentEntry = useCallback(
        async (tournament: Tournament) => {
            if (!currentUser) {
                message.error(t("beerpong.tournament.messages.signInToUpdate"));
                return;
            }
            setDeletingTournamentId(tournament.tournamentId);
            try {
                await deleteTournament(tournament.tournamentId, currentUser);
                setTournaments((prev) =>
                    prev.filter((candidate) => candidate.tournamentId !== tournament.tournamentId)
                );
                message.success(t("beerpong.tournament.messages.deleteSuccess"));
            } catch (error) {
                console.error("Failed to delete tournament", error);
                message.error(t("beerpong.tournament.messages.deleteError"));
            } finally {
                setDeletingTournamentId(null);
            }
        },
        [currentUser, t, message]
    );

    return {
        tournaments,
        loadingTournaments,
        creatingTournament,
        updatingMatchId,
        updatingTournamentId,
        unlockingTournamentId,
        eliminatingTeamId,
        advancingTournamentId,
        restoringTeamId,
        deletingTournamentId,
        loadTournaments,
        createTournamentEntry,
        updateMatchWinner,
        updateMatchTeams,
        updateTournamentStatus,
        updateTournamentStyle,
        updateTournamentVisibility,
        eliminateTournamentTeam,
        restoreTournamentTeam,
        advanceTournamentBracket,
        unlockTournamentDetails,
        deleteTournamentEntry,
    };
};
